import { Check, User } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs, Container, NavItem, Section } from '../Common/Layout';
import { useBrandColor } from '../../common/Common/ThemeProvider';
import { useNavigation } from '../Common/useNavigation';
import { useRegistrationsUrlState } from '../Dashboard/Registrations/helpers';
import GetEventQuery from '../../frontend/Common/GetEventQuery';
// eslint-disable-next-line max-len
import GetRegistrationSummaryForDashboardQuery from '../Dashboard/Registrations/GetRegistrationSummaryForDashboardQuery';
import Guard from '../Dashboard/Guard';
import UI from '../../common/UI';
import UpgradesForm from '../Dashboard/Registrations/Upgrades/UpgradesForm';
import useDocumentTitle from '../../common/useDocumentTitle';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

export interface ClaimSuccessPageProps {
  participantId: string;
  eventId: string;
}

const ClaimSuccessPage = ({
  participantId,
  eventId,
}: ClaimSuccessPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const project = useProject();
  const [urlState] = useRegistrationsUrlState();

  const {
    data: summaryData, loading,
  } = useQuery(
    GetRegistrationSummaryForDashboardQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token: null,
        registrations_limit: urlState.registrations.limit,
        registration_filters: {
          assigned: true,
        },
        preregistrations_limit: 0,
        waitlist_registrations_limit: 0,
        teams_limit: 0,
        include_filtered_teams: false,
      },
    },
  );

  const { data: eventData } = useQuery(
    GetEventQuery,
    {
      variables: {
        id: eventId,
        invitationCode: null,
        coupon: null,
      },
    },
  );

  const event = eventData?.event;

  useDocumentTitle([
    t('ticket_claimed'),
    event?.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(event?.brand_color);

  if (!summaryData || !eventData || !project || !themeInitialized) {
    return (
      <UI.PageLoader />
    );
  }

  return (
    <Guard scopedParticipant={{ id: participantId }}>
      <Container>
        <Breadcrumbs hideHome={false}>
          <UI.Link to={r('Registrations', {
            projectId: project.id,
            eventId: summaryData.registrationSummary.event.id,
            participantId: summaryData.registrationSummary.participant.id,
            token: summaryData.registrationSummary.view_token,
          })}
          >
            {summaryData.registrationSummary.event.title}
          </UI.Link>
          <UI.Strong>{t('ticket_claimed')}</UI.Strong>
        </Breadcrumbs>

        <UI.GridContainer>
          <UI.Div>
            <UI.H1 sc={{ fontSize: 4, color: 'secondary' }}>
              <UI.Icon sc={{ big: true, mr: 1 }}>
                <Check />
              </UI.Icon>
              {' '}
              {t('ticket_claimed_title')}
            </UI.H1>
          </UI.Div>
        </UI.GridContainer>

        <Section title={t('ticket_claimed')} intro={t('ticket_claimed_description')}>
          <NavItem
            to={r('Registrations', {
              projectId: project.id,
              eventId: summaryData.registrationSummary.event.id,
              participantId: summaryData.registrationSummary.participant.id,
              token: summaryData.registrationSummary.view_token,
            })}
            title={t('frontend:success_page.go_to_dashboard')}
          >
            <UI.GridContainer sc={{ columns: '36px 1fr', gutter: 0.5 }}>
              <UI.Div>
                <UI.Badge sc={{ size: 36, mt: 0.75 }}>
                  <UI.Icon sc={{ size: 1.125 }}>
                    <User />
                  </UI.Icon>
                </UI.Badge>
              </UI.Div>
              <UI.Div>
                <UI.Strong sc={{ block: true }}>
                  {t('frontend:success_page.go_to_dashboard')}
                </UI.Strong>
                {t('frontend:success_page.go_to_dashboard_description')}
              </UI.Div>
            </UI.GridContainer>
          </NavItem>
        </Section>

        {summaryData.registrationSummary.has_upgrades_available && (
          <UpgradesForm
            summary={summaryData.registrationSummary}
            loadingSummary={loading}
            event={event}
          />
        )}
      </Container>
    </Guard>
  );
};

export default ClaimSuccessPage;
